import React, { useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { apiPost, apiPut } from '../../lib/api';

const inputCls =
  'w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#1F7A6E] focus:ring-1 focus:ring-[#1F7A6E] focus:outline-none';

export default function MachineForm({ initial, onClose, onSaved }) {
  const editing = !!initial?.id;
  const [f, setF] = useState({
    eq_id: initial?.eq_id || '',
    type: initial?.type || '',
    make_model: initial?.make_model || initial?.make || '',
    status: initial?.status || 'Idle',
    ownership: initial?.ownership || 'owned',
    purchase_date: (initial?.purchase_date || '').slice(0, 10),
    purchase_value: initial?.purchase_value ?? '',
    salvage_value: initial?.salvage_value ?? '',
    useful_life_years: initial?.useful_life_years ?? '',
    depreciation_method: initial?.depreciation_method || 'slm',
    wdv_rate_pct: initial?.wdv_rate_pct ?? '',
    internal_hire_rate_per_hour: initial?.internal_hire_rate_per_hour ?? '',
    current_hour_meter: initial?.current_hour_meter ?? 0,
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = {
      ...f,
      purchase_value: f.purchase_value === '' ? null : Number(f.purchase_value),
      salvage_value: f.salvage_value === '' ? null : Number(f.salvage_value),
      useful_life_years: f.useful_life_years === '' ? null : Number(f.useful_life_years),
      wdv_rate_pct: f.wdv_rate_pct === '' ? null : Number(f.wdv_rate_pct),
      internal_hire_rate_per_hour: f.internal_hire_rate_per_hour === '' ? null : Number(f.internal_hire_rate_per_hour),
      purchase_date: f.purchase_date || null,
    };
    try {
      if (editing) {
        delete body.current_hour_meter; // hour meter changes go through /hour-meter only
        await apiPut(`/equipment/${initial.id}`, body);
      } else {
        body.current_hour_meter = Number(f.current_hour_meter) || 0;
        await apiPost('/equipment', body);
      }
      toast.success(editing ? 'Machine updated' : 'Machine added to fleet');
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={save} onClick={e => e.stopPropagation()} data-testid="machine-form"
        className="bg-white rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <h3 className="font-display font-bold text-lg">{editing ? `Edit ${initial.eq_id}` : 'Add Machine'}</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <Field label="Equipment ID *">
            <input value={f.eq_id} onChange={set('eq_id')} className={inputCls} data-testid="eq-form-eqid" required disabled={editing} />
          </Field>
          <Field label="Type *">
            <input value={f.type} onChange={set('type')} className={inputCls} data-testid="eq-form-type" required placeholder="Batching Plant / Transit Mixer / Wheel Loader…" />
          </Field>
          <Field label="Make & Model">
            <input value={f.make_model} onChange={set('make_model')} className={inputCls} data-testid="eq-form-make" />
          </Field>
          <Field label="Status">
            <select value={f.status} onChange={set('status')} className={`${inputCls} bg-white`} data-testid="eq-form-status">
              {['Working', 'Idle', 'Down', 'Maintenance'].map(s => <option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Ownership">
            <select value={f.ownership} onChange={set('ownership')} className={`${inputCls} bg-white`} data-testid="eq-form-ownership">
              <option value="owned">Owned</option>
              <option value="hired">Hired</option>
            </select>
          </Field>
          {!editing && (
            <Field label="Current Hour Meter">
              <input type="number" min="0" step="0.1" value={f.current_hour_meter} onChange={set('current_hour_meter')} className={`${inputCls} tabular-nums`} data-testid="eq-form-hm" />
            </Field>
          )}

          <div className="col-span-2 kpi-overline mt-2">Purchase & Depreciation</div>
          <Field label="Purchase Date">
            <input type="date" value={f.purchase_date} onChange={set('purchase_date')} className={inputCls} data-testid="eq-form-pdate" />
          </Field>
          <Field label="Purchase Value (₹)">
            <input type="number" min="0" value={f.purchase_value} onChange={set('purchase_value')} className={`${inputCls} tabular-nums`} data-testid="eq-form-pvalue" />
          </Field>
          <Field label="Salvage Value (₹)">
            <input type="number" min="0" value={f.salvage_value} onChange={set('salvage_value')} className={`${inputCls} tabular-nums`} data-testid="eq-form-salvage" />
          </Field>
          <Field label="Depreciation Method">
            <select value={f.depreciation_method} onChange={set('depreciation_method')} className={`${inputCls} bg-white`} data-testid="eq-form-depmethod">
              <option value="slm">SLM — Straight Line</option>
              <option value="wdv">WDV — Written Down Value</option>
            </select>
          </Field>
          {f.depreciation_method === 'slm' ? (
            <Field label="Useful Life (years)">
              <input type="number" min="1" value={f.useful_life_years} onChange={set('useful_life_years')} className={`${inputCls} tabular-nums`} data-testid="eq-form-life" />
            </Field>
          ) : (
            <Field label="WDV Rate % (e.g. 15 for P&M)">
              <input type="number" min="0" max="100" step="0.5" value={f.wdv_rate_pct} onChange={set('wdv_rate_pct')} className={`${inputCls} tabular-nums`} data-testid="eq-form-wdvrate" />
            </Field>
          )}
          <Field label="Internal Hire Rate (₹/hour)">
            <input type="number" min="0" value={f.internal_hire_rate_per_hour} onChange={set('internal_hire_rate_per_hour')} className={`${inputCls} tabular-nums`} data-testid="eq-form-hirerate" />
          </Field>
        </div>
        <div className="p-5 border-t border-slate-200 flex justify-end gap-2 sticky bottom-0 bg-white">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-sm">Cancel</button>
          <button type="submit" disabled={busy} data-testid="eq-form-save"
            className="px-4 py-2 text-sm bg-[#1F7A6E] text-white rounded-sm font-display font-semibold disabled:opacity-50">
            {busy ? 'Saving…' : editing ? 'Save Changes' : 'Add Machine'}
          </button>
        </div>
      </form>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">{label}</label>
    {children}
  </div>
);
